import type { PatternFormField, PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isNotNull } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { Pattern } from '~/common/model/api/batch-type/pattern';

import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

const logger = createLogger('logic/feature/settings/batch-type-library');

// * Transformation to text for naming rule
const IS_TRANSFORMATION_TEXT = true;

/**
 * Create Pattern from NamingRule FormFieldItem.
 *
 * @param {PatternFormField} formField NamingRule FormFieldItem.
 * @param {PatternAccessScope} scope Access scope for creating pattern.
 *
 * @returns {Pattern[]} NamingRule Pattern.
 */
export function createNamingRulePattern(formField: PatternFormField, scope: PatternAccessScope): Pattern[] {
  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (typeof item === 'string') {
      pattern = patternLogic.createPattern.fromText(item);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create NamingRule FormFieldItem from Pattern.
 *
 * @param {Pattern[]} namingRulePattern Pattern.
 *
 * @returns {PatternFormField} NamingRule FormFieldItem.
 */
export function createNamingRuleFormFieldItem(namingRulePattern: Pattern[]): PatternFormField {
  return namingRulePattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}
